// @flow

import * as React from 'react';
import useSheet from 'app/jss';
import { css } from 'glamor';
import { connect } from 'react-redux';
import * as cssEase from 'app/constants/cssEase';
import * as UiActions from 'app/actions/uiActions';
import classNames from 'classnames';
import chatPathSelector from 'app/selectors/chat/chatPathSelector';
import Chat from 'app/components/chat/Chat';

type Props = {
  classes: Object,
  framePath?: string,
  isOpen: boolean,
  isNativeApp: boolean,
  toggleChat: () => void
};

const noScroll = css({
  left: 0,
  position: 'fixed',
  right: 0,
  top: 0
}).toString();

const defaultProps = {
  framePath: undefined
};

function MobileChat({ isOpen, framePath, classes, toggleChat, isNativeApp }: Props) {
  const handleToggleChat = React.useCallback(toggleChat, []);

  React.useEffect(() => {
    if (isOpen) {
      document.body.classList.add(noScroll);
    } else {
      document.body.classList.remove(noScroll);
    }

    return () => {
      document.body.classList.remove(noScroll);
    };
  }, [isOpen]);

  // comment regarding the scrolling="no" and width: 1px/min-width: 100%;
  // Mobile safari is dumb when it comes to iframes and width: 100% ignores
  // the container and uses the full landscape width of the phone always.
  // The combination of the 3 properties above solves this.
  return (
    <div className={classNames(classes.chatWrapper, { isOpen, isNativeApp })}>
      <Chat onCloseClick={handleToggleChat}>
        {isOpen && (
          <iframe scrolling="no" className={classes.chat} src={framePath} title="chat dialog" />
        )}
      </Chat>
    </div>
  );
}

MobileChat.defaultProps = defaultProps;

function applyStyles(theme: Theme) {
  return {
    chatWrapper: {
      position: 'fixed',
      right: 0,
      bottom: 0,
      left: '15%',
      top: 0,
      overflow: 'hidden',
      zIndex: theme.zIndex.chat,
      transform: 'translate3d(100%, 0, 0)',
      transition: 'transform 0.20s',
      transitionTimingFunction: cssEase.Circ.easeInOut,
      backgroundColor: theme.color.white,
      display: 'flex',
      flexDirection: 'column',
      '&.isOpen': {
        transform: 'translate3d(0%, 0, 0)'
      },
      '&.isNativeApp': {
        top: '90px'
      }
    },
    chat: {
      border: 0,
      display: 'block',
      overflow: 'hidden',
      width: 1,
      minWidth: '100%',
      flexGrow: 1
    }
  };
}

function mapStateToProps(state: GlobalState) {
  return {
    isOpen: state.ui.isChatOpen,
    framePath: chatPathSelector(state),
    isNativeApp: !!state.settings.nativeApp
  };
}

const actions = {
  toggleChat: UiActions.toggleChat
};

export default connect(
  mapStateToProps,
  actions
)(useSheet(applyStyles)(MobileChat));
